import ReactFlow, { Background, Controls } from "reactflow";
import { WorkflowStepDto } from "@babackapp/private-api-client/types";
import { useCallback, useMemo } from "react";
import { NewNode } from "./workflow-nodes/new-node";
import TriggerNode from "./workflow-nodes/trigger-node";
import ActionNode from "./workflow-nodes/action-node";
import "reactflow/dist/style.css";

type FlowProps = {
  steps: WorkflowStepDto[];
  selectedStep?: WorkflowStepDto;
  onSelect: (step: WorkflowStepDto) => void;
  newStep: () => WorkflowStepDto;
  onDelete: (stepId: string) => void;
};

const nodeTypes = { action: ActionNode, new: NewNode, trigger: TriggerNode };

function Flow({ steps, selectedStep, onSelect, newStep, onDelete }: FlowProps) {
  const handleNewStep = useCallback(() => {
    const step = newStep();
    onSelect(step);
  }, [onSelect, newStep]);

  const [nodes, edges] = useMemo(() => {
    const createNode = (step: WorkflowStepDto, index: number) => ({
      id: index.toString(),
      data: {
        label: step.type,
        step,
        onSelect,
        onDelete,
        isSelected: selectedStep?.id === step.id,
      },
      position: { x: 0, y: 200 * index },
      type: step.type === "ACTION" ? "action" : "trigger",
    });

    const createEdge = (sourceIndex: number, targetIndex: number) => ({
      id: `${sourceIndex}-${targetIndex}`,
      type: "step",
      source: sourceIndex.toString(),
      target: targetIndex.toString(),
    });

    const initialNodes = steps.map((step, index) => createNode(step, index));
    const initialEdges = steps
      .slice(0, -1)
      .map((_, index) => createEdge(index, index + 1));

    const newNode = {
      id: steps.length.toString(),
      data: {
        label: "action",
        newStep: handleNewStep,
      },
      position: { x: 0, y: 200 * steps.length },
      type: "new",
    };

    const newEdge = {
      id: `${steps.length - 1}-${steps.length}`,
      type: "step",
      source: (steps.length - 1).toString(),
      target: steps.length.toString(),
    };

    return [
      [...initialNodes, newNode],
      [...initialEdges, newEdge],
    ];
  }, [steps, handleNewStep, onSelect, onDelete, selectedStep]);

  return (
    <div style={{ height: "100%" }}>
      <ReactFlow
        defaultViewport={{ x: 500, y: 200, zoom: 1 }}
        nodeOrigin={[0.5, 0.5]}
        nodeTypes={nodeTypes}
        nodes={nodes}
        edges={edges}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}

export default Flow;
